import React from "react";
import {
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import {
  SceneShell,
  SlideChip,
  SlideTitle,
  Card3D,
  Punchline,
  DefBox,
  PlainBox,
  Pill,
  glow,
  spAppear,
} from "./shared";

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;

/* ════════════ Slide 01 — Title ════════════ */
export const S01Title: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonCyan;

  const tSp = spring({ frame, fps, config: SPRING.bouncy });
  const tOp = interpolate(tSp, [0, 1], [0, 1], clamp);
  const tSc = interpolate(tSp, [0, 1], [0.64, 1], clamp);
  const subOp = spAppear(frame, fps, 26);
  const dateOp = spAppear(frame, fps, 46);
  const tagOp = spAppear(frame, fps, 62);
  const ringRot = frame * 0.55;
  const glyphs = ["🧠", "⚙️", "📚", "🔧", "💬", "🔁", "🛰️", "✦", "🪪", "📈", "🧩", "⚡"];

  let extrude = "";
  for (let i = 1; i <= 11; i++) extrude += `${i}px ${i}px 0 ${COLORS.bgCardLight}, `;

  return (
    <SceneShell accent={accent}>
      {/* orbiting 3D ground ring of glyphs */}
      <div
        style={{
          position: "absolute",
          top: "54%",
          left: "50%",
          width: 0,
          height: 0,
          perspective: 1600,
        }}
      >
        <div style={{ transform: `rotateX(${64 + Math.sin(frame * 0.015) * 5}deg)` }}>
          {glyphs.map((g, i) => {
            const ang = ((ringRot + i * (360 / glyphs.length)) * Math.PI) / 180;
            const x = Math.cos(ang) * 780;
            const y = Math.sin(ang) * 780;
            const front = Math.sin(ang) > 0;
            return (
              <div
                key={i}
                style={{
                  position: "absolute",
                  left: x - 32,
                  top: y - 32,
                  fontSize: 58,
                  opacity: tOp * (front ? 0.95 : 0.32),
                  transform: `rotateX(-64deg) scale(${front ? 1 : 0.66})`,
                  filter: `drop-shadow(0 0 18px ${accent}dd)`,
                }}
              >
                {g}
              </div>
            );
          })}
        </div>
      </div>

      {/* centre title block */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 6,
        }}
      >
        <div
          style={{
            opacity: dateOp,
            transform: `translateY(${(1 - dateOp) * -16}px)`,
            marginBottom: 26,
          }}
        >
          <Pill text="◷  24 MAY 2026" color={accent} solid size={20} />
        </div>

        <div
          style={{
            fontSize: 96,
            fontFamily: FONTS.heading,
            fontWeight: 900,
            color: COLORS.textPrimary,
            textAlign: "center",
            lineHeight: 1.05,
            letterSpacing: -2.5,
            opacity: tOp,
            transform: `scale(${tSc})`,
            textShadow: extrude + glow(accent, true),
          }}
        >
          Building <span style={{ color: COLORS.neonAmber }}>Hermes</span>-Inspired
          <br />
          Capabilities into <span style={{ color: accent }}>EAKC</span>
        </div>

        <div
          style={{
            fontSize: 30,
            fontFamily: FONTS.body,
            color: COLORS.textSecondary,
            marginTop: 26,
            opacity: subOp,
            fontWeight: 500,
            letterSpacing: 0.5,
          }}
        >
          How an enterprise knowledge companion learned to learn.
        </div>

        <div
          style={{
            display: "flex",
            gap: 16,
            marginTop: 34,
            opacity: tagOp,
            transform: `translateY(${(1 - tagOp) * 20}px)`,
          }}
        >
          <Pill text="30 ANIMATED SLIDES" color={COLORS.neonPurple} />
          <Pill text="SKILLS · KINDS · UI · CHAT" color={COLORS.neonPink} />
          <Pill text="GROUNDED IN REAL WORK" color={COLORS.neonGreen} />
        </div>
      </div>
    </SceneShell>
  );
};

/* ════════════ Slide 02 — The 2026 shift ════════════ */
export const S02Shift: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonBlue;
  const ap = spAppear(frame, fps);

  const panels = [
    {
      title: "Agents that ANSWER",
      tag: "REACTIVE",
      icon: "💬",
      c: COLORS.neonOrange,
      rotY: 17,
      lines: ["Every task starts from zero", "Yesterday's fix is forgotten", "Quality never compounds"],
    },
    {
      title: "Agents that LEARN",
      tag: "SELF-IMPROVING",
      icon: "🧠",
      c: COLORS.neonGreen,
      rotY: -17,
      lines: ["Each finished task becomes a lesson", "Lessons are reused automatically", "Quality compounds run over run"],
    },
  ];

  const sweep = interpolate(frame, [40, 110], [0, 1], clamp);

  return (
    <SceneShell accent={accent}>
      <SlideChip n={2} color={accent} appear={ap} label="The trend" />
      <SlideTitle
        text="The 2026 shift"
        subtitle="The market is moving from agents that answer to agents that learn"
        color={accent}
        appear={ap}
      />

      <div
        style={{
          position: "absolute",
          top: 296,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          gap: 120,
          perspective: 1700,
        }}
      >
        {panels.map((p, i) => {
          const sp = spring({ frame: frame - 16 - i * 16, fps, config: SPRING.bouncy });
          const op = interpolate(sp, [0, 1], [0, 1], clamp);
          const sc = interpolate(sp, [0, 1], [0.6, 1], clamp);
          const lift = i === 1 ? interpolate(sweep, [0, 1], [0, -22], clamp) : 0;
          return (
            <div
              key={i}
              style={{
                width: 560,
                height: 322,
                opacity: op,
                transform: `translateY(${lift + Math.sin(frame * 0.025 + i) * 6}px) scale(${sc}) perspective(1500px) rotateY(${p.rotY}deg)`,
                transformStyle: "preserve-3d",
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: 24,
                  background: `linear-gradient(145deg, ${p.c}26, ${COLORS.bgCard}f2)`,
                  border: `2.5px solid ${p.c}`,
                  boxShadow: `0 0 46px ${p.c}${i === 1 ? "88" : "44"}, 0 26px 50px rgba(0,0,0,0.55)`,
                  padding: 30,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <div style={{ fontSize: 64 }}>{p.icon}</div>
                  <div>
                    <div
                      style={{
                        fontSize: 30,
                        fontFamily: FONTS.heading,
                        fontWeight: 900,
                        color: p.c,
                        textShadow: glow(p.c),
                      }}
                    >
                      {p.title}
                    </div>
                    <div
                      style={{
                        fontSize: 14,
                        fontFamily: FONTS.mono,
                        color: COLORS.textMuted,
                        letterSpacing: 2.5,
                        marginTop: 3,
                      }}
                    >
                      {p.tag}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 12 }}>
                  {p.lines.map((l, j) => (
                    <div
                      key={j}
                      style={{
                        fontSize: 20,
                        fontFamily: FONTS.body,
                        color: COLORS.textSecondary,
                        display: "flex",
                        gap: 12,
                        opacity: interpolate(frame, [30 + i * 14 + j * 10, 52 + i * 14 + j * 10], [0, 1], clamp),
                      }}
                    >
                      <span style={{ color: p.c, fontWeight: 900 }}>{i === 0 ? "✕" : "✓"}</span>
                      {l}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
        {/* sweep arrow */}
        <div
          style={{
            position: "absolute",
            top: 142,
            left: "50%",
            transform: "translateX(-50%)",
            fontSize: 56,
            opacity: sweep,
            filter: `drop-shadow(0 0 16px ${COLORS.neonGreen})`,
          }}
        >
          ➜
        </div>
      </div>

      <div
        style={{
          position: "absolute",
          top: 678,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          gap: 44,
        }}
      >
        <DefBox
          term="Self-improving agent"
          body="An agent that turns each completed task into a durable, reusable lesson — so quality compounds instead of resetting."
          color={accent}
          appear={spAppear(frame, fps, 70)}
          width={680}
        />
        <PlainBox
          body="A normal assistant forgets the trick the second it finishes. A learning one keeps a note — and is a little sharper next time."
          appear={spAppear(frame, fps, 86)}
          width={580}
        />
      </div>
    </SceneShell>
  );
};

/* ════════════ Slide 03 — Meet Hermes ════════════ */
export const S03Hermes: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonAmber;
  const ap = spAppear(frame, fps);
  const tiles = [...Array(24)];

  return (
    <SceneShell accent={accent}>
      <SlideChip n={3} color={accent} appear={ap} label="The trend" />
      <SlideTitle
        text="Meet Hermes"
        subtitle="Nous Research's agent — a 24-tool surface that keeps notes on itself"
        color={accent}
        appear={ap}
      />

      {/* left — 24-tool grid tilted in 3D */}
      <div
        style={{
          position: "absolute",
          top: 318,
          left: 150,
          width: 660,
          height: 420,
          perspective: 1500,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(6, 1fr)",
            gap: 14,
            transform: `rotateX(20deg) rotateY(-16deg) rotateZ(2deg)`,
            transformStyle: "preserve-3d",
          }}
        >
          {tiles.map((_, i) => {
            const sp = spring({ frame: frame - 14 - i * 3, fps, config: SPRING.snappy });
            const op = interpolate(sp, [0, 1], [0, 1], clamp);
            const tz = interpolate(sp, [0, 1], [-160, 0], clamp);
            const cols = [COLORS.neonAmber, COLORS.neonOrange, COLORS.neonYellow, COLORS.neonPink];
            const c = cols[i % cols.length];
            return (
              <div
                key={i}
                style={{
                  height: 88,
                  borderRadius: 12,
                  background: `linear-gradient(140deg, ${c}33, ${COLORS.bgCard}f0)`,
                  border: `1.6px solid ${c}`,
                  boxShadow: `0 0 16px ${c}66`,
                  opacity: op,
                  transform: `translateZ(${tz + Math.sin(frame * 0.03 + i) * 10}px)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 30,
                }}
              >
                {["🔍", "🌐", "💻", "👁️", "🔧", "🧠"][i % 6]}
              </div>
            );
          })}
        </div>
        <div
          style={{
            position: "absolute",
            bottom: -8,
            left: 0,
            opacity: spAppear(frame, fps, 40),
          }}
        >
          <Pill text="24-TOOL CAPABILITY SURFACE" color={accent} />
        </div>
      </div>

      {/* right — the skill idea */}
      <div style={{ position: "absolute", top: 320, right: 130, width: 720 }}>
        <Card3D w={720} h={196} color={accent} frame={frame} delay={30} fps={fps} inline rotY={-7}>
          <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
            <div style={{ fontSize: 76 }}>📝</div>
            <div>
              <div
                style={{
                  fontSize: 27,
                  fontFamily: FONTS.heading,
                  fontWeight: 900,
                  color: accent,
                  textShadow: glow(accent),
                }}
              >
                The "skill" — Hermes' core trick
              </div>
              <div
                style={{
                  fontSize: 19,
                  fontFamily: FONTS.body,
                  color: COLORS.textSecondary,
                  marginTop: 8,
                  lineHeight: 1.42,
                }}
              >
                When a task needs roughly five or more tool calls, Hermes writes
                itself a small markdown note — <i>how I solved that</i> — and reads
                it back next time a similar problem appears.
              </div>
            </div>
          </div>
        </Card3D>

        <div style={{ marginTop: 22 }}>
          <DefBox
            term="Hermes skill"
            body={
              <>
                A markdown file at{" "}
                <span style={{ fontFamily: FONTS.mono, color: accent }}>
                  ~/.hermes/skills/&lt;slug&gt;/SKILL.md
                </span>{" "}
                holding a reusable how-to, plus usage and provenance metadata.
              </>
            }
            color={accent}
            appear={spAppear(frame, fps, 52)}
            width={720}
          />
        </div>
      </div>

      <div style={{ position: "absolute", bottom: 56, left: 0, right: 0, display: "flex", justifyContent: "center" }}>
        <PlainBox
          body="Hermes is a pocket-knife with 24 blades — and a notebook of 'here's how I cracked that one,' so it never re-solves the same puzzle."
          appear={spAppear(frame, fps, 74)}
          width={1180}
        />
      </div>
    </SceneShell>
  );
};

/* ════════════ Slide 04 — Continual Harness ════════════ */
export const S04Harness: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonPurple;
  const ap = spAppear(frame, fps);

  // static loop — nodes fixed on an ellipse, a pulse travels the ring
  const cx = 540;
  const cy = 486;
  const rx = 250;
  const ry = 150;
  const nodes = [
    { label: "AI plays", icon: "🎮", c: COLORS.neonBlue, a: -90 },
    { label: "Analyse the run", icon: "🔬", c: COLORS.neonCyan, a: 0 },
    { label: "Edit 4 components", icon: "✍️", c: COLORS.neonPink, a: 90 },
    { label: "Continue — never resets", icon: "♾️", c: COLORS.neonGreen, a: 180 },
  ];
  const pr = ((frame * 1.25) % 360) * (Math.PI / 180);
  const pulseX = cx + Math.cos(pr) * rx;
  const pulseY = cy + Math.sin(pr) * ry;

  return (
    <SceneShell accent={accent}>
      <SlideChip n={4} color={accent} appear={ap} label="The trend" />
      <SlideTitle
        text="The field already agrees"
        subtitle="Princeton's Continual Harness — the same shape, independently"
        color={accent}
        appear={ap}
      />

      {/* dashed loop ring */}
      <svg style={{ position: "absolute", inset: 0, pointerEvents: "none" }} width={1920} height={1080}>
        <ellipse
          cx={cx}
          cy={cy}
          rx={rx}
          ry={ry}
          fill="none"
          stroke={`${accent}aa`}
          strokeWidth={3}
          strokeDasharray="11 13"
          strokeDashoffset={-(frame * 1.5) % 24}
          style={{ filter: `drop-shadow(0 0 6px ${accent})` }}
        />
      </svg>

      {/* travelling pulse */}
      <div
        style={{
          position: "absolute",
          left: pulseX - 13,
          top: pulseY - 13,
          width: 26,
          height: 26,
          borderRadius: "50%",
          background: COLORS.textPrimary,
          boxShadow: `0 0 18px ${COLORS.textPrimary}, 0 0 38px ${accent}`,
          opacity: spAppear(frame, fps, 38),
        }}
      />

      {/* static nodes on the ring */}
      {nodes.map((n, i) => {
        const rad = (n.a * Math.PI) / 180;
        const x = cx + Math.cos(rad) * rx;
        const y = cy + Math.sin(rad) * ry;
        const sp = spring({ frame: frame - 16 - i * 11, fps, config: SPRING.bouncy });
        const op = interpolate(sp, [0, 1], [0, 1], clamp);
        const sc = interpolate(sp, [0, 1], [0.4, 1], clamp);
        const floatY = Math.sin(frame * 0.03 + i) * 5;
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: x - 96,
              top: y - 64 + floatY,
              width: 192,
              height: 128,
              opacity: op,
              transform: `scale(${sc}) perspective(900px) rotateY(${Math.sin(frame * 0.012 + i) * 8}deg)`,
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                borderRadius: 18,
                background: `linear-gradient(140deg, ${n.c}30, ${COLORS.bgCard}f2)`,
                border: `2.4px solid ${n.c}`,
                boxShadow: `0 0 30px ${n.c}66`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 7,
              }}
            >
              <div style={{ fontSize: 42 }}>{n.icon}</div>
              <div
                style={{
                  fontSize: 15,
                  fontFamily: FONTS.heading,
                  fontWeight: 800,
                  color: n.c,
                  textAlign: "center",
                  textShadow: glow(n.c),
                  padding: "0 10px",
                  lineHeight: 1.2,
                }}
              >
                {n.label}
              </div>
            </div>
          </div>
        );
      })}

      {/* centre label */}
      <div
        style={{
          position: "absolute",
          left: cx - 140,
          top: cy - 40,
          width: 280,
          textAlign: "center",
          fontSize: 23,
          fontFamily: FONTS.heading,
          fontWeight: 900,
          color: accent,
          textShadow: glow(accent, true),
          opacity: spAppear(frame, fps, 30),
        }}
      >
        self-improves
        <br />
        while it plays
      </div>

      {/* right column — what it does + honest note */}
      <div style={{ position: "absolute", top: 300, right: 110, width: 600 }}>
        <Card3D w={600} h={224} color={accent} frame={frame} delay={24} fps={fps} inline rotY={-8}>
          <div
            style={{
              fontSize: 21,
              fontFamily: FONTS.heading,
              fontWeight: 900,
              color: accent,
              textShadow: glow(accent),
            }}
          >
            An AI that learns while playing Pokémon
          </div>
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 9 }}>
            {[
              "Rewrites its own system prompt",
              "Spawns specialised sub-agents",
              "Builds a reusable skill library",
              "Keeps persistent memory — never resets",
            ].map((t, i) => (
              <div
                key={i}
                style={{
                  fontSize: 17,
                  fontFamily: FONTS.body,
                  color: COLORS.textSecondary,
                  display: "flex",
                  gap: 10,
                  opacity: spAppear(frame, fps, 34 + i * 9),
                }}
              >
                <span style={{ color: accent }}>▸</span>
                {t}
              </div>
            ))}
          </div>
        </Card3D>

        <div
          style={{
            marginTop: 16,
            opacity: spAppear(frame, fps, 78),
            background: `${COLORS.neonOrange}14`,
            border: `1.6px solid ${COLORS.neonOrange}99`,
            borderRadius: 14,
            padding: "13px 20px",
          }}
        >
          <div
            style={{
              fontSize: 14,
              fontFamily: FONTS.mono,
              fontWeight: 800,
              letterSpacing: 2,
              color: COLORS.neonOrange,
            }}
          >
            ⚠ THE HONEST READ
          </div>
          <div style={{ fontSize: 16, color: COLORS.textSecondary, marginTop: 6, lineHeight: 1.4 }}>
            The autonomy is real but domain-narrow. The load-bearing safety piece
            is a capability threshold — not magic.
          </div>
        </div>
      </div>

      <div style={{ position: "absolute", bottom: 48, left: 0, right: 0, display: "flex", justifyContent: "center" }}>
        <PlainBox
          body="Two separate teams built the same kind of self-learning machine. Different playground — identical idea. EAKC is not over-designing."
          appear={spAppear(frame, fps, 92)}
          width={1160}
        />
      </div>
    </SceneShell>
  );
};

/* ════════════ Slide 05 — The groundhog-day problem ════════════ */
export const S05Groundhog: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonRed;
  const ap = spAppear(frame, fps);

  const patches = [
    "Patched the Glean agent's prompt",
    "Added a workflow for one mission",
    "Edited a mission template by hand",
    "Tweaked a macro in TypeScript",
    "Patched the next agent's prompt…",
  ];

  return (
    <SceneShell accent={accent}>
      <SlideChip n={5} color={accent} appear={ap} label="The trend" />
      <SlideTitle
        text="The groundhog-day problem"
        subtitle="Every fix is local — so the same class of problem returns tomorrow"
        color={accent}
        appear={ap}
      />

      {/* quote */}
      <div
        style={{
          position: "absolute",
          top: 300,
          left: 130,
          width: 700,
          opacity: spAppear(frame, fps, 14),
          transform: `perspective(1300px) rotateY(9deg)`,
        }}
      >
        <div
          style={{
            background: `linear-gradient(140deg, ${accent}22, ${COLORS.bgCard}f2)`,
            border: `2.5px solid ${accent}`,
            borderRadius: 22,
            padding: "34px 36px",
            boxShadow: `0 0 44px ${accent}55, 0 24px 46px rgba(0,0,0,0.55)`,
          }}
        >
          <div style={{ fontSize: 78, color: accent, lineHeight: 0.4, marginBottom: 18 }}>“</div>
          <div
            style={{
              fontSize: 38,
              fontFamily: FONTS.heading,
              fontWeight: 800,
              color: COLORS.textPrimary,
              lineHeight: 1.28,
            }}
          >
            I'm tired of patching the same things every day.
          </div>
          <div
            style={{
              fontSize: 18,
              fontFamily: FONTS.mono,
              color: COLORS.textMuted,
              marginTop: 18,
              letterSpacing: 1,
            }}
          >
            — the operator, before the pivot
          </div>
        </div>
      </div>

      {/* stacking band-aid cards */}
      <div style={{ position: "absolute", top: 286, right: 180, width: 560, height: 420 }}>
        {patches.map((p, i) => {
          const sp = spring({ frame: frame - 30 - i * 16, fps, config: SPRING.bouncy });
          const op = interpolate(sp, [0, 1], [0, 1], clamp);
          const drop = interpolate(sp, [0, 1], [-180, 0], clamp);
          const c = [COLORS.neonOrange, COLORS.neonYellow, COLORS.neonPink, COLORS.neonRed, COLORS.neonOrange][i];
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                top: i * 74 + drop,
                left: (i % 2) * 40,
                width: 470,
                height: 64,
                opacity: op,
                transform: `perspective(1100px) rotateY(-10deg) rotate(${(i - 2) * 2.4}deg)`,
                display: "flex",
                alignItems: "center",
                gap: 14,
                padding: "0 20px",
                borderRadius: 12,
                background: `linear-gradient(135deg, ${c}2e, ${COLORS.bgCard}f0)`,
                border: `2px solid ${c}`,
                boxShadow: `0 0 22px ${c}66, 0 10px 22px rgba(0,0,0,0.5)`,
              }}
            >
              <div style={{ fontSize: 30 }}>🩹</div>
              <div style={{ fontSize: 18, fontFamily: FONTS.body, color: COLORS.textSecondary, fontWeight: 500 }}>
                {p}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ position: "absolute", bottom: 220, left: 130, width: 700 }}>
        <DefBox
          term="Band-aid fix"
          body="A change scoped to one agent or one mission. The next mission of a similar shape hits the same class of problem all over again."
          color={accent}
          appear={spAppear(frame, fps, 60)}
          width={700}
        />
      </div>

      <Punchline
        text={
          <>
            Fixing one robot never teaches the{" "}
            <span style={{ color: accent }}>others</span>.
          </>
        }
        color={accent}
        appear={spAppear(frame, fps, 96)}
      />
    </SceneShell>
  );
};

/* ════════════ Slide 06 — Meet EAKC ════════════ */
export const S06MeetEAKC: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const accent = COLORS.neonCyan;
  const ap = spAppear(frame, fps);

  const stages = [
    { icon: "📥", label: "Ingest", sub: "repos & docs", c: COLORS.neonBlue },
    { icon: "🧩", label: "Structural chunk", sub: "smart splitting", c: COLORS.neonPurple },
    { icon: "🔎", label: "Hybrid search", sub: "pgvector + keyword", c: COLORS.neonCyan },
    { icon: "✦", label: "Grounded answer", sub: "with citations", c: COLORS.neonGreen },
  ];

  return (
    <SceneShell accent={accent}>
      <SlideChip n={6} color={accent} appear={ap} label="The trend" />
      <SlideTitle
        text={
          <>
            Meet <span style={{ color: accent }}>EAKC</span>
          </>
        }
        subtitle="Enterprise AI Knowledge Companion — a shared brain for a whole team"
        color={accent}
        appear={ap}
      />

      {/* surface pills */}
      <div
        style={{
          position: "absolute",
          top: 250,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          gap: 14,
          opacity: spAppear(frame, fps, 18),
        }}
      >
        <Pill text="💬 CHAT UI" color={COLORS.neonPink} />
        <Pill text="🔌 MCP SERVER" color={COLORS.neonAmber} />
        <Pill text="🏢 MULTI-TENANT" color={COLORS.neonPurple} />
      </div>

      {/* isometric pipeline */}
      <div
        style={{
          position: "absolute",
          top: 328,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 26,
          perspective: 1700,
        }}
      >
        {stages.map((s, i) => (
          <React.Fragment key={i}>
            <Card3D w={272} h={224} color={s.c} frame={frame} delay={18 + i * 14} fps={fps} inline rotY={(i - 1.5) * 9}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  gap: 12,
                }}
              >
                <div style={{ fontSize: 74 }}>{s.icon}</div>
                <div
                  style={{
                    fontSize: 23,
                    fontFamily: FONTS.heading,
                    fontWeight: 900,
                    color: s.c,
                    textShadow: glow(s.c),
                    textAlign: "center",
                  }}
                >
                  {s.label}
                </div>
                <div style={{ fontSize: 15, fontFamily: FONTS.mono, color: COLORS.textMuted }}>{s.sub}</div>
              </div>
            </Card3D>
            {i < stages.length - 1 && (
              <div
                style={{
                  fontSize: 40,
                  color: accent,
                  opacity: spAppear(frame, fps, 40 + i * 14),
                  filter: `drop-shadow(0 0 12px ${accent})`,
                }}
              >
                ➜
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      <div
        style={{
          position: "absolute",
          top: 620,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          gap: 44,
        }}
      >
        <DefBox
          term="RAG — Retrieval-Augmented Generation"
          body="The model first retrieves the most relevant stored passages, then answers using them — so every claim is grounded and cited."
          color={accent}
          appear={spAppear(frame, fps, 64)}
          width={680}
        />
        <PlainBox
          body="Ask EAKC a question. It reads your real files, then answers with receipts — not from memory, not guessing."
          appear={spAppear(frame, fps, 80)}
          width={560}
        />
      </div>
    </SceneShell>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
